import { supabase } from './supabase';
import type { ModuleCompletion, Driver, TrainingModule, Quiz, Question, QuestionOption, AuditLog } from '../types';
import { generateCertificatePDF } from '../utils/certificate';

// Companies API
export const companies = {
  list: async () => {
    try {
      console.log('Fetching companies...');
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .eq('active', true);

      if (error) throw error;
      return data;
    } catch (err: any) {
      console.error('Error fetching companies:', err);
      // Return default company if fetch fails
      return [{
        id: 'f8c3de3d-1fea-4d7c-a8b0-29f63c4c3454',
        name: 'Zumm Delivery',
        logo: 'https://zummdelivery.com.br/wp-content/uploads/2023/01/admin-ajax-1.png',
        primaryColor: '#2563eb',
        secondaryColor: '#1e40af',
        active: true,
        createdAt: new Date().toISOString()
      }];
    }
  }
};

// Modules API
export const modules = {
  list: async () => {
    try {
      console.log('Fetching modules...');
      const { data, error } = await supabase
        .from('modules')
        .select(`
          id,
          title,
          description,
          video_url,
          duration,
          order_index,
          thumbnail_url,
          active,
          created_at,
          updated_at,
          quiz:quizzes (
            id,
            questions (
              id,
              text,
              type,
              options,
              explanation,
              points,
              required
            )
          )
        `)
        .order('order_index', { ascending: true });

      if (error) {
        console.error('Error fetching modules:', error);
        throw error;
      }

      console.log('Modules data:', data);

      // Return mock data if no modules found
      if (!data || data.length === 0) {
        return [
          {
            id: '1',
            title: 'Como Funciona o Pagamento e Seus Ganhos',
            description: 'Aprenda sobre o sistema de pagamento, cálculo de taxas e como gerenciar seus ganhos no aplicativo.',
            videoUrl: 'https://www.youtube.com/watch?v=mBbzlYlAFgw',
            duration: 1200,
            order: 1,
            thumbnail: 'https://images.unsplash.com/photo-1580519542036-c47de6196ba5?w=1200&h=600&fit=crop',
            active: true,
            createdAt: new Date().toISOString(),
            quiz: {
              id: 'mock-quiz-1',
              questions: [
                {
                  id: 'q1',
                  text: 'Como você deve proceder ao receber um pedido?',
                  type: 'single',
                  options: [
                    { id: 'opt1', text: 'Verificar detalhes antes de aceitar', isCorrect: true },
                    { id: 'opt2', text: 'Aceitar imediatamente', isCorrect: false }
                  ],
                  points: 1,
                  required: true
                }
              ]
            }
          }
        ];
      }

      return data.map(module => ({
        id: module.id,
        title: module.title,
        description: module.description,
        videoUrl: module.video_url,
        duration: module.duration,
        order: module.order_index,
        thumbnail: module.thumbnail_url,
        active: module.active,
        createdAt: module.created_at,
        updatedAt: module.updated_at,
        quiz: module.quiz ? {
          id: module.quiz.id,
          moduleId: module.id,
          questions: module.quiz.questions?.map(q => ({
            ...q,
            options: Array.isArray(q.options) ? q.options : []
          })) || []
        } : null
      }));
    } catch (err: any) {
      console.error('Error in modules.list:', err);
      throw new Error(err.message || 'Não foi possível carregar os módulos. Por favor, tente novamente.');
    }
  }
};

// Quiz API
export const quizzes = {
  get: async (moduleId: string) => {
    try {
      console.log('Fetching quiz for module:', moduleId);
      const { data, error } = await supabase
        .from('quizzes')
        .select(`
          id,
          module_id,
          questions (
            id,
            text,
            type,
            options,
            explanation,
            points,
            required
          )
        `)
        .eq('module_id', moduleId)
        .single();

      if (error) {
        console.error('Error fetching quiz:', error);
        throw error;
      }

      console.log('Quiz data:', data);
      return data;
    } catch (err: any) {
      console.error('Error in quiz.get:', err);
      throw new Error('Não foi possível carregar o questionário. Por favor, tente novamente.');
    }
  },

  create: async (moduleId: string, questions: Omit<Question, 'id'>[]) => {
    try {
      console.log('Creating quiz for module:', moduleId, 'with questions:', questions);
      
      // First create the quiz
      const { data: quiz, error: quizError } = await supabase
        .from('quizzes')
        .insert({ module_id: moduleId })
        .select()
        .single();

      if (quizError) {
        console.error('Error creating quiz:', quizError);
        throw quizError;
      }

      console.log('Quiz created:', quiz);

      // Then create all questions
      const { data: questionsData, error: questionsError } = await supabase
        .from('questions')
        .insert(
          questions.map(q => ({
            quiz_id: quiz.id,
            text: q.text,
            type: q.type,
            options: q.options,
            explanation: q.explanation,
            points: q.points,
            required: q.required
          }))
        )
        .select();

      if (questionsError) {
        console.error('Error creating questions:', questionsError);
        throw questionsError;
      }

      console.log('Questions created:', questionsData);

      return {
        ...quiz,
        questions: questionsData
      };
    } catch (err: any) {
      console.error('Error in quiz.create:', err);
      throw new Error(err.message || 'Não foi possível criar o questionário. Por favor, tente novamente.');
    }
  },

  update: async (quizId: string, questions: Question[]) => {
    try {
      console.log('Updating quiz:', quizId, 'with questions:', questions);

      // First delete all existing questions
      const { error: deleteError } = await supabase
        .from('questions')
        .delete()
        .eq('quiz_id', quizId);

      if (deleteError) {
        console.error('Error deleting existing questions:', deleteError);
        throw deleteError;
      }

      console.log('Existing questions deleted');

      // Then create new questions
      const { data: questionsData, error: questionsError } = await supabase
        .from('questions')
        .insert(
          questions.map(q => ({
            quiz_id: quizId,
            text: q.text,
            type: q.type,
            options: q.options,
            explanation: q.explanation,
            points: q.points,
            required: q.required
          }))
        )
        .select();

      if (questionsError) {
        console.error('Error creating new questions:', questionsError);
        throw questionsError;
      }

      console.log('New questions created:', questionsData);
      return questionsData;
    } catch (err: any) {
      console.error('Error in quiz.update:', err);
      throw new Error(err.message || 'Não foi possível atualizar o questionário. Por favor, tente novamente.');
    }
  },

  submitAnswer: async (quizId: string, answers: {
    questionId: string;
    selectedOptions: string[];
  }[]) => {
    try {
      // Get quiz questions with correct answers
      const { data: quiz, error: quizError } = await supabase
        .from('quizzes')
        .select(`
          id,
          questions (
            id,
            text,
            type,
            options,
            points,
            required
          )
        `)
        .eq('id', quizId)
        .single();

      if (quizError) throw quizError;

      // Calculate score
      let totalPoints = 0;
      let earnedPoints = 0;

      quiz.questions.forEach((question: Question) => {
        const answer = answers.find(a => a.questionId === question.id);
        if (!answer && question.required) {
          throw new Error('Todas as questões obrigatórias devem ser respondidas.');
        }

        const points = question.points || 1;
        totalPoints += points;

        if (answer) {
          const correctOptions = question.options
            .filter(opt => opt.isCorrect)
            .map(opt => opt.id);

          const isCorrect = question.type === 'single'
            ? answer.selectedOptions[0] === correctOptions[0]
            : answer.selectedOptions.length === correctOptions.length &&
              answer.selectedOptions.every(opt => correctOptions.includes(opt));

          if (isCorrect) {
            earnedPoints += points;
          }
        }
      });

      const score = (earnedPoints / totalPoints) * 100;

      return {
        score,
        details: answers.map(answer => {
          const question = quiz.questions.find((q: Question) => q.id === answer.questionId);
          return {
            questionId: answer.questionId,
            question: question.text,
            selectedOptions: answer.selectedOptions,
            correctOptions: question.options
              .filter(opt => opt.isCorrect)
              .map(opt => opt.id),
            isCorrect: answer.selectedOptions.every(opt => 
              question.options.find(o => o.id === opt)?.isCorrect
            )
          };
        })
      };
    } catch (err: any) {
      console.error('Error submitting answers:', err);
      throw new Error(err.message || 'Erro ao enviar respostas. Por favor, tente novamente.');
    }
  }
};

// Certificates API
export const certificates = {
  list: async () => {
    try {
      console.log('Fetching certificates...');
      const { data, error } = await supabase
        .from('module_completions')
        .select(`
          id,
          driver:drivers (
            id,
            name,
            email,
            cpf,
            city,
            phone
          ),
          module:modules (
            id,
            title
          ),
          score,
          status,
          rejection_reason,
          module_results,
          completed_at,
          created_at,
          updated_at
        `)
        .order('completed_at', { ascending: false });

      if (error) throw error;

      // Filter to keep only the most recent entry per CPF and status
      const uniqueEntries = data?.reduce((acc: any[], curr) => {
        if (!curr.driver) return acc;
        
        // For pending status, always show
        if (curr.status === 'pending') {
          // Check if we already have a more recent pending entry for this CPF
          const existingPending = acc.find(item => 
            item.driver?.cpf === curr.driver.cpf && 
            item.status === 'pending' &&
            new Date(item.completed_at) > new Date(curr.completed_at)
          );
          
          if (!existingPending) {
            acc.push(curr);
          }
          return acc;
        }
        
        // For approved/rejected, only show if not already in list with same status
        const existingIndex = acc.findIndex(item => 
          item.driver?.cpf === curr.driver.cpf && 
          item.status === curr.status
        );
        
        if (existingIndex === -1) {
          acc.push(curr);
        }
        
        return acc;
      }, []);

      return uniqueEntries || [];
    } catch (err: any) {
      console.error('Error fetching certificates:', err);
      throw new Error('Não foi possível carregar os certificados. Por favor, tente novamente.');
    }
  },
  
  update: async (id: string, status: 'approved' | 'rejected', rejectionReason?: string) => {
    try {
      // First, check if the certificate still exists and is pending
      const { data: existing, error: checkError } = await supabase
        .from('module_completions')
        .select('*')
        .eq('id', id)
        .single();

      if (checkError) throw checkError;
      if (!existing) throw new Error('Certificado não encontrado');
      if (existing.status !== 'pending') {
        throw new Error('Este certificado já foi processado anteriormente');
      }

      // Update completion status
      const { error: updateError } = await supabase
        .from('module_completions')
        .update({ 
          status,
          rejection_reason: rejectionReason || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (updateError) throw updateError;

    } catch (err: any) {
      console.error('Error updating certificate:', err);
      throw new Error(err.message || 'Não foi possível atualizar o status. Por favor, tente novamente.');
    }
  },

  generatePDF: async (driverId: string) => {
    try {
      // Get driver data
      const { data: driver, error: driverError } = await supabase
        .from('drivers')
        .select('*')
        .eq('id', driverId)
        .single();

      if (driverError) throw new Error('Motorista não encontrado');

      // Get company data
      const { data: company, error: companyError } = await supabase
        .from('companies')
        .select('*')
        .eq('id', driver.company_id)
        .single();

      if (companyError) throw new Error('Empresa não encontrada');

      // Get completions
      const { data: completions, error: completionsError } = await supabase
        .from('module_completions')
        .select('*, module:modules(*)')
        .eq('driver_id', driverId);

      if (completionsError) throw new Error('Erro ao carregar dados do treinamento');
      if (!completions?.length) throw new Error('Nenhum módulo completado encontrado');

      const averageScore = completions.reduce((acc, curr) => acc + curr.score, 0) / completions.length;

      const pdfBlob = await generateCertificatePDF({
        driver,
        company,
        score: averageScore,
        completedAt: completions[0].completed_at,
      });

      const certificatePath = `drivers/${driverId}/certificate.pdf`;
      
      const { error: uploadError } = await supabase
        .storage
        .from('certificates')
        .upload(certificatePath, pdfBlob, {
          contentType: 'application/pdf',
          upsert: true
        });

      if (uploadError) throw new Error('Erro ao salvar o certificado');

      const { data: urlData } = await supabase
        .storage
        .from('certificates')
        .createSignedUrl(certificatePath, 300);

      if (!urlData?.signedUrl) throw new Error('Erro ao gerar link do certificado');

      return { signedUrl: urlData.signedUrl };
    } catch (err: any) {
      console.error('Error generating certificate:', err);
      throw new Error(err.message || 'Não foi possível gerar o certificado. Por favor, tente novamente.');
    }
  },

  getActivities: async () => {
    try {
      const { data, error } = await supabase
        .from('audit_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      return data || [];
    } catch (err: any) {
      console.error('Error fetching activities:', err);
      return [];
    }
  }
};

// Drivers API
export const drivers = {
  create: async (driver: Omit<Driver, 'id' | 'createdAt'>, moduleCompletions: Omit<ModuleCompletion, 'id' | 'driverId' | 'createdAt'>[]) => {
    try {
      const { data: newDriver, error: driverError } = await supabase
        .from('drivers')
        .insert({
          name: driver.name,
          email: driver.email,
          cpf: driver.cpf.replace(/\D/g, ''),
          phone: driver.phone.replace(/\D/g, ''),
          city: driver.city,
          company_id: driver.companyId
        })
        .select()
        .single();

      if (driverError) throw driverError;

      if (moduleCompletions.length > 0) {
        const { error: completionsError } = await supabase
          .from('module_completions')
          .insert(
            moduleCompletions.map(completion => ({
              driver_id: newDriver.id,
              module_id: completion.moduleId,
              score: completion.score,
              status: 'pending',
              completed_at: completion.completedAt
            }))
          );

        if (completionsError) throw completionsError;
      }

      const { signedUrl } = await certificates.generatePDF(newDriver.id);
      return { driver: newDriver, certificateUrl: signedUrl };
    } catch (err: any) {
      console.error('Error creating driver:', err);
      throw new Error('Não foi possível salvar os dados. Por favor, tente novamente.');
    }
  }
};

export default {
  companies,
  modules,
  quizzes,
  certificates,
  drivers
};